import { useRef, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { API_URL, RELOAD_STORY } from "./config";
import { StoryDescriptor, StoryMultilingual } from "./story";
import { UnauthorizedError, getAuthToken, isLoggedInSettled } from "./firebase";
import { escapeExplanationText } from "./explanationMarkup";
import type { LocalizationStrings } from "./localization";

// HttpError is a non-2xx answer of the API. code is the machine-readable
// reason the API sends for some failures as {"error": "<code>"}, else null.
export class HttpError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
  ) {
    super(`HTTP ${status}${code ? ` (${code})` : ""}`);
    this.name = "HttpError";
  }
}

// errorCode reads the machine-readable code of an error answer
// ({"error": "<code>"}), or null when there is none.
async function errorCode(res: Response): Promise<string | null> {
  const body = await res.json().catch(() => null);
  return body && typeof body.error === "string" ? body.error : null;
}

// httpErrorFromResponse logs a failed API answer and returns the HttpError
// to throw for it.
async function httpErrorFromResponse(
  url: URL,
  res: Response,
): Promise<HttpError> {
  const code = await errorCode(res);
  console.error("Unexpected result for", url.pathname, res.status, code ?? "");
  return new HttpError(res.status, code);
}

// isClientHttpError tells whether error is a 4xx answer, which a repeat of
// the same request won't change.
export function isClientHttpError(error: unknown): boolean {
  return (
    error instanceof HttpError && error.status >= 400 && error.status < 500
  );
}

// shouldRetryQuery is the default retry policy of every query: a client
// error or a missing sign-in won't change on a repeat, while a 5xx answer or
// a network failure may be transient.
export function shouldRetryQuery(failureCount: number, error: unknown) {
  if (error instanceof UnauthorizedError) return false;
  if (isClientHttpError(error)) return false;
  return failureCount < 2;
}

export async function fetchParamsWithAuth(method: string) {
  return getAuthToken().then((token) => {
    return {
      method: method,
      headers: new Headers({
        Authorization: `Bearer ${token}`,
      }),
    };
  });
}

async function fetchParamsWithOptionalAuth(method: string) {
  // Await the initial Firebase auth hydration: a synchronous check here could
  // be stale on page load and produce an unauthenticated request for a
  // resource that requires the token (e.g. a generated story).
  return (await isLoggedInSettled())
    ? fetchParamsWithAuth(method)
    : { method: method };
}

export function apiUrl(path: string) {
  if (!path.startsWith("/") || path.includes("..")) {
    throw new Error("Invalid path");
  }
  return new URL(`${API_URL}${path}`);
}

export function useStoryListQuery(l: string, r: string) {
  let url = new URL(`${API_URL}/story-list`);
  url.searchParams.append("l", l);
  url.searchParams.append("r", r);

  return useQuery<StoryDescriptor[]>({
    queryKey: ["story-list", l, r],
    queryFn: async () =>
      fetchParamsWithOptionalAuth("GET").then((params) =>
        fetch(url, params).then(async (res) => {
          if (res.status === 200) {
            return res.json();
          }
          throw await httpErrorFromResponse(url, res);
        }),
      ),
  });
}

export function useGeneratedStoryListQuery(l: string, r: string) {
  let url = new URL(`${API_URL}/generated-list`);
  url.searchParams.append("l", l);
  url.searchParams.append("r", r);

  return useQuery<StoryDescriptor[]>({
    // l and r must be part of the key: the request URL depends on them, and a
    // locale-agnostic key would serve one language pair's cached list to
    // another after a settings change.
    queryKey: ["generated-story-list", l, r],
    queryFn: async () =>
      fetchParamsWithAuth("GET").then((params) =>
        fetch(url, params).then(async (res) => {
          if (res.status === 200) {
            return res.json();
          }
          throw await httpErrorFromResponse(url, res);
        }),
      ),
  });
}

// Thrown by useScanMutation and useUploadMutation when the backend determines
// that the user input doesn't contain a meaningful amount of target-language
// text. The UI uses this to show a specific message instead of a generic one.
export class NoTargetLanguageError {}

// Thrown by the three long-running mutations (generate/scan/upload) when the
// caller aborts the AbortController. We surface this as a typed error so the
// view can silently reset the mutation state instead of showing a generic
// "something went wrong" message. NOTE: the backend aborts its in-flight LLM
// call once it sees the disconnect, and until it has stopped refuses a new
// request with RequestInProgressError.
export class CancelledError {}

// isAbortError detects the DOMException that fetch() throws when its signal
// is aborted. We accept both the DOMException form (modern browsers) and the
// legacy `error.name === "AbortError"` form for safety.
function isAbortError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === "AbortError") return true;
  if (
    err &&
    typeof err === "object" &&
    "name" in err &&
    (err as { name?: unknown }).name === "AbortError"
  ) {
    return true;
  }
  return false;
}

// Thrown by useScanMutation, useUploadMutation, and useGenerateStoryMutation
// when the backend safety gate rejects the input (pasted text, text
// extracted from scanned images, or custom generation instructions). The
// backend deliberately reports prompt-injection and content-policy
// rejections under this single generic code so an attacker gets no
// confirmation that an injection attempt was specifically detected.
export class DisallowedContentError {}

// Thrown by useGenerateStoryMutation when the backend decides the custom
// instructions are not a plausible story-shaping request but an attempt to
// use the generator as a general-purpose LLM (e.g. coding or factual
// queries). The UI shows a message explaining what the field is for.
export class OffTopicInstructionsError {}

// Thrown by useGenerateStoryMutation, useScanMutation and useUploadMutation
// when the backend refuses to start the request: the user already started
// today's limit of this kind of request, another of their requests is still
// running, or the server is too busy (including nginx's per-IP rate limit).
// Views show them with refusalMessage.
class DailyLimitReachedError {
  constructor(readonly verifyingEmailRaisesLimit: boolean) {}
}
class RequestInProgressError {}
class ServerBusyError {}

// refusalMessage is the message for a refusal to start a generate/scan/upload
// request (see throwIfRefused), or null for any other error.
export function refusalMessage(
  err: unknown,
  strings: LocalizationStrings,
): string | null {
  if (err instanceof DailyLimitReachedError)
    return err.verifyingEmailRaisesLimit
      ? strings.create_error_daily_limit_reached_unverified
      : strings.create_error_daily_limit_reached;
  if (err instanceof RequestInProgressError)
    return strings.create_error_request_in_progress;
  if (err instanceof ServerBusyError) return strings.create_error_server_busy;
  return null;
}

// throwIfRefused throws the matching error above when the backend refused to
// start a generate/scan/upload request.
async function throwIfRefused(res: Response) {
  if (res.status !== 429 && res.status !== 503) return;
  // The API's writeQuotaRefusal writes the quota refusals.
  const body = await res.json().catch(() => null);
  if (body?.error === "daily_limit_reached")
    throw new DailyLimitReachedError(body.email_verified === false);
  if (body?.error === "request_in_progress") throw new RequestInProgressError();
  throw new ServerBusyError();
}

// storyQueryKey is useStoryQuery's key, for reading a story from the cache.
export function storyQueryKey(storyId: string, l: string, r: string) {
  return ["story", storyId, l, r];
}

export function useStoryQuery(storyId: string, l: string, r: string) {
  let url = new URL(`${API_URL}/story`);
  url.searchParams.append("id", storyId);
  url.searchParams.append("l", l);
  url.searchParams.append("r", r);

  const refetchInterval = RELOAD_STORY ? 5000 : false;

  return useQuery<StoryMultilingual>({
    queryKey: storyQueryKey(storyId, l, r),
    queryFn: async () =>
      fetchParamsWithOptionalAuth("GET").then((params) =>
        fetch(url, params).then(async (res) => {
          if (res.ok) {
            return res.json().then((obj) => {
              return {
                id: obj.id,
                localizations: new Map(Object.entries(obj.localizations)),
                favorite: obj.favorite ?? false,
                read: obj.read ?? false,
              };
            });
          }
          throw await httpErrorFromResponse(url, res);
        }),
      ),
    refetchInterval: refetchInterval,
    gcTime: 1000 * 60 * 240, // 2 hours
    staleTime: 1000 * 60 * 240, // 2 hours
  });
}

export function useGenerateStoryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      l,
      r,
      level,
      moods,
      topics,
      instructions,
      signal,
    }: {
      // l is the optional mother-tongue locale; omit it (empty string) to
      // generate a story only in the learned language.
      l: string;
      r: string;
      level: string;
      moods: string[];
      topics: string[];
      // Optional free-text instructions shaping the story (max
      // MAX_INSTRUCTIONS_CHARS characters, validated again server-side).
      instructions: string;
      // Optional AbortSignal so the caller can cancel the in-flight request.
      signal?: AbortSignal;
    }) => {
      let url = new URL(`${API_URL}/generate`);
      if (l) {
        url.searchParams.append("l", l);
      }
      url.searchParams.append("r", r);
      url.searchParams.append("level", level);
      // Left out when nothing is chosen, so the backend picks for the user.
      if (moods.length > 0) {
        url.searchParams.append("moods", moods.join(","));
      }
      if (topics.length > 0) {
        url.searchParams.append("topics", topics.join(","));
      }
      if (instructions) {
        url.searchParams.append("instructions", instructions);
      }

      const params = await fetchParamsWithAuth("POST");
      try {
        const res = await fetch(url, { ...params, signal });
        queryClient.invalidateQueries({ queryKey: ["generated-story-list"] });
        await throwIfRefused(res);
        if (res.ok) {
          const obj = await res.json();
          return {
            id: obj.id,
            localizations: new Map(Object.entries(obj.localizations)),
          };
        } else if (res.status === 422) {
          const code = await errorCode(res);
          if (code === "disallowed_content") throw new DisallowedContentError();
          if (code === "off_topic_instructions")
            throw new OffTopicInstructionsError();
          throw new Error("Unexpected 422 result for generate");
        } else {
          console.error("Unexpected result for", url, res);
          throw new Error("Unexpected result for story generate mutation");
        }
      } catch (err) {
        if (isAbortError(err)) throw new CancelledError();
        throw err;
      }
    },
    retry: false,
  });
}

export function useScanMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      images,
      r,
      signal,
    }: {
      images: File[];
      r: string;
      signal?: AbortSignal;
    }) => {
      if (images.length === 0) {
        throw new Error("No images selected for scan");
      }

      const formData = new FormData();
      for (const img of images) {
        formData.append("images", img, img.name);
      }

      const url = apiUrl("/scan");
      url.searchParams.append("r", r);
      const token = await getAuthToken();
      try {
        // Note: do NOT set Content-Type manually here - the browser must add
        // the multipart boundary to the header.
        const res = await fetch(url, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
          signal,
        });
        queryClient.invalidateQueries({ queryKey: ["generated-story-list"] });

        await throwIfRefused(res);
        if (res.status === 422) {
          const code = await errorCode(res);
          if (code === "disallowed_content") throw new DisallowedContentError();
          if (code === "no_target_language") throw new NoTargetLanguageError();
          throw new Error("Unexpected 422 result for scan");
        }
        if (!res.ok) {
          console.error("Unexpected result for", url, res);
          throw new Error("Unexpected result for scan mutation");
        }
        const obj = await res.json();
        return {
          id: obj.id as string,
          localizations: new Map(Object.entries(obj.localizations)),
        };
      } catch (err) {
        if (isAbortError(err)) throw new CancelledError();
        throw err;
      }
    },
    retry: false,
  });
}

// useUploadMutation drives the user-pasted-text ingest flow. The request is a
// small JSON body so we send it directly (no multipart). The backend may
// return a 422 with one of two specific error codes; we surface each as a
// dedicated typed error so the UI can localize messages.
export function useUploadMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      text,
      r,
      signal,
    }: {
      text: string;
      r: string;
      signal?: AbortSignal;
    }) => {
      const url = apiUrl("/upload");
      url.searchParams.append("r", r);
      const token = await getAuthToken();
      try {
        const res = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ text }),
          signal,
        });
        queryClient.invalidateQueries({ queryKey: ["generated-story-list"] });

        await throwIfRefused(res);
        if (res.status === 422) {
          const code = await errorCode(res);
          if (code === "disallowed_content") throw new DisallowedContentError();
          if (code === "no_target_language") throw new NoTargetLanguageError();
          throw new Error("Unexpected 422 result for upload");
        }
        if (!res.ok) {
          console.error("Unexpected result for", url, res);
          throw new Error("Unexpected result for upload mutation");
        }
        const obj = await res.json();
        return {
          id: obj.id as string,
          localizations: new Map(Object.entries(obj.localizations)),
        };
      } catch (err) {
        if (isAbortError(err)) throw new CancelledError();
        throw err;
      }
    },
    retry: false,
  });
}

// StoryMark names a per-user story mark; each is a boolean field of the same
// name on StoryDescriptor and StoryMultilingual.
export type StoryMark = "favorite" | "read";

// The endpoints that put each story mark on and take it off.
const STORY_MARK_ENDPOINTS: Record<StoryMark, { add: string; remove: string }> = {
  favorite: { add: "/favorite-story", remove: "/unfavorite-story" },
  read: { add: "/mark-story-read", remove: "/unmark-story-read" },
};

// setStoryMarkInCache sets one mark of one story in place in every cached
// story list and cached story, without reordering anything.
function setStoryMarkInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  storyId: string,
  mark: StoryMark,
  marked: boolean,
) {
  const setInList = (old: StoryDescriptor[] | undefined) =>
    old?.map((s) => (s.id === storyId ? { ...s, [mark]: marked } : s));
  queryClient.setQueriesData<StoryDescriptor[]>(
    { queryKey: ["story-list"] },
    setInList,
  );
  queryClient.setQueriesData<StoryDescriptor[]>(
    { queryKey: ["generated-story-list"] },
    setInList,
  );
  queryClient.setQueriesData<StoryMultilingual>(
    { queryKey: ["story", storyId] },
    (old) => (old ? { ...old, [mark]: marked } : old),
  );
}

// useSetStoryMarkMutation puts a per-user mark on a story (generated or
// curated) or takes it off. The mark flips optimistically on tap, in place:
// the server sorts the lists by marks, but re-sorting the visible list would
// move rows under the user's finger, so the lists are only marked stale and
// pick up the new order on the next visit. A failed request reverts the mark
// and calls onFailure - for every failed toggle, unlike callbacks passed to
// mutate(), which TanStack runs only for the latest call.
export function useSetStoryMarkMutation(
  mark: StoryMark,
  onFailure?: () => void,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      storyId,
      marked,
    }: {
      storyId: string;
      marked: boolean;
    }) => {
      const endpoints = STORY_MARK_ENDPOINTS[mark];
      const url = apiUrl(marked ? endpoints.add : endpoints.remove);
      url.searchParams.append("story_id", storyId);

      const params = await fetchParamsWithAuth(marked ? "POST" : "DELETE");
      const res = await fetch(url, params);
      if (!res.ok) {
        console.error("Unexpected result for", url, res);
        throw new Error(`Unexpected result for ${mark} story mark mutation`);
      }
    },
    // All toggles of a mark share one scope: every tap applies its optimistic
    // update immediately, but the requests reach the server one at a time in
    // tap order, so rapid toggling ends in the state the user chose last.
    scope: { id: `story-mark-${mark}` },
    onMutate: async ({ storyId, marked }) => {
      // An in-flight refetch would overwrite the optimistic flag.
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ["story-list"] }),
        queryClient.cancelQueries({ queryKey: ["generated-story-list"] }),
        queryClient.cancelQueries({ queryKey: ["story", storyId] }),
      ]);
      setStoryMarkInCache(queryClient, storyId, mark, marked);
    },
    onError: (_err, { storyId, marked }) => {
      setStoryMarkInCache(queryClient, storyId, mark, !marked);
      onFailure?.();
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["story-list"],
        refetchType: "none",
      });
      queryClient.invalidateQueries({
        queryKey: ["generated-story-list"],
        refetchType: "none",
      });
    },
    retry: false,
  });
}

// A story rating is 1 to MAX_STORY_RATING stars, see storymark.MaxRating on
// the server.
export const MAX_STORY_RATING = 5;

// useStoryRatingQuery loads the signed-in user's rating of a story; null when
// they haven't rated it.
export function useStoryRatingQuery(storyId: string) {
  return useQuery<number | null>({
    queryKey: ["story-rating", storyId],
    queryFn: async () => {
      const url = apiUrl("/story-rating");
      url.searchParams.append("story_id", storyId);

      const params = await fetchParamsWithAuth("GET");
      const res = await fetch(url, params);
      if (!res.ok) {
        console.error("Unexpected result for", url, res);
        throw new Error("Unexpected result for story rating query");
      }
      const body: { rating: number | null } = await res.json();
      return body.rating;
    },
  });
}

// useRateStoryMutation stores the user's rating of a story. The rating shows
// optimistically on tap, and the story's ratings share one scope, so they
// reach the server in tap order. Only the last rating in flight settles what
// is shown, as a later tap overrides an earlier one: if it failed, the
// previous rating comes back right away. Either way the server's rating is
// then refetched, since the previous rating may itself have been an
// optimistic one that failed, and a refetch started meanwhile (e.g. on window
// focus) may have overwritten the optimistic rating.
export function useRateStoryMutation(storyId: string) {
  const queryClient = useQueryClient();
  const ratingQueryKey = ["story-rating", storyId];
  const rateMutationKey = ["rate-story", storyId];
  // The settling mutation itself still counts as pending in its callbacks.
  const isLastPendingRating = () =>
    queryClient.isMutating({ mutationKey: rateMutationKey }) === 1;
  return useMutation({
    mutationKey: rateMutationKey,
    mutationFn: async (rating: number) => {
      const url = apiUrl("/rate-story");
      url.searchParams.append("story_id", storyId);
      url.searchParams.append("rating", String(rating));

      const params = await fetchParamsWithAuth("POST");
      const res = await fetch(url, params);
      if (!res.ok) {
        console.error("Unexpected result for", url, res);
        throw new Error("Unexpected result for rate story mutation");
      }
    },
    scope: { id: `rate-story:${storyId}` },
    onMutate: async (rating) => {
      // An in-flight fetch would overwrite the optimistic rating.
      await queryClient.cancelQueries({ queryKey: ratingQueryKey });
      const previousRating =
        queryClient.getQueryData<number | null>(ratingQueryKey) ?? null;
      queryClient.setQueryData<number | null>(ratingQueryKey, rating);
      return { previousRating };
    },
    onError: (_err, _rating, context) => {
      if (isLastPendingRating()) {
        queryClient.setQueryData<number | null>(
          ratingQueryKey,
          context?.previousRating ?? null,
        );
      }
    },
    onSettled: () => {
      if (isLastPendingRating()) {
        queryClient.invalidateQueries({ queryKey: ratingQueryKey });
      }
    },
    retry: false,
  });
}

export function useDeleteStoryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (storyId: string) => {
      let url = apiUrl("/delete-generated");
      url.searchParams.append("story_id", storyId);

      const params = await fetchParamsWithAuth("DELETE");
      const res = await fetch(url, params);
      if (!res.ok) {
        throw new Error("Failed to delete story");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["generated-story-list"] });
    },
  });
}

// markWordSavedInCache flips alreadySaved on every cached word-explanation that
// resolved to this dictionary entry. The explain query carries the saved state,
// so without this a save/remove would be forgotten as soon as the popup is
// reopened from cache. Keying off the entry id (not the click position) also
// keeps other sentences' explanations of the same word in sync.
function markWordSavedInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  dictionaryEntryId: number,
  saved: boolean,
) {
  queryClient.setQueriesData<WordExplanation>(
    { queryKey: ["explain-word"] },
    (old) => {
      if (!old) return old;
      let updated = old;
      if (old.dictionaryEntryId === dictionaryEntryId) {
        updated = { ...updated, alreadySaved: saved };
      }
      if (old.phrase?.dictionaryEntryId === dictionaryEntryId) {
        updated = { ...updated, phrase: { ...old.phrase, alreadySaved: saved } };
      }
      return updated;
    },
  );
}

// SAVE_WORD_LIMIT_ERROR is the Error message thrown by useSaveWordMutation when
// the backend rejects a save because the user hit their saved-word cap (HTTP
// 409). The UI checks for it to show a specific "limit reached" message.
export const SAVE_WORD_LIMIT_ERROR = "save-word-limit-reached";

// StorySentence is a sentence of a story in the learned language r.
export interface StorySentence {
  storyId: string;
  r: string;
  rSentenceIdx: number;
}

// useSaveWordMutation adds an already-ingested dictionary sense to the user's
// saved-word list. The dictionary entry was created for the word's explanation
// by the API; here we just send its id plus the spoken language l (used to
// generate the localized description in the background) and the story
// sentence it was saved from, if any (practice can then draw from that
// story's words). The backend returns immediately.
export function useSaveWordMutation() {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    {
      dictionaryEntryId: number;
      l: string;
      storySentence?: StorySentence;
    }
  >({
    mutationFn: async ({ dictionaryEntryId, l, storySentence }) => {
      const url = apiUrl("/save-word");
      url.searchParams.append("dictionary_entry_id", dictionaryEntryId.toString());
      url.searchParams.append("l", l);
      if (storySentence) {
        url.searchParams.append("story_id", storySentence.storyId);
        url.searchParams.append("r", storySentence.r);
        url.searchParams.append(
          "r_sentence_idx",
          storySentence.rSentenceIdx.toString(),
        );
      }

      const params = await fetchParamsWithAuth("POST");
      const res = await fetch(url, params);
      if (!res.ok) {
        // 409 means the per-user saved-word cap is reached; surface it as a
        // distinct, actionable message instead of the generic save error.
        if (res.status === 409) {
          throw new Error(SAVE_WORD_LIMIT_ERROR);
        }
        console.error("Unexpected result for", url, res);
        throw new Error("Unexpected result for save-word mutation");
      }
    },
    onSuccess: (_data, { dictionaryEntryId }) =>
      markWordSavedInCache(queryClient, dictionaryEntryId, true),
    retry: false,
  });
}

// useRemoveWordMutation removes a dictionary sense from the user's saved-word
// list. Only the user reference is dropped; the global dictionary entry stays.
export function useRemoveWordMutation() {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    {
      dictionaryEntryId: number;
    }
  >({
    mutationFn: async ({ dictionaryEntryId }) => {
      const url = apiUrl("/remove-word");
      url.searchParams.append("dictionary_entry_id", dictionaryEntryId.toString());

      const params = await fetchParamsWithAuth("DELETE");
      const res = await fetch(url, params);
      if (!res.ok) {
        console.error("Unexpected result for", url, res);
        throw new Error("Unexpected result for remove-word mutation");
      }
    },
    onSuccess: (_data, { dictionaryEntryId }) => {
      markWordSavedInCache(queryClient, dictionaryEntryId, false);
      // Drop the word from any cached My Dictionary page so the list reflects
      // the removal (e.g. when deleting from the dictionary page itself).
      queryClient.invalidateQueries({ queryKey: ["my-dictionary"] });
    },
    retry: false,
  });
}

// How a sentence is read aloud: "slow" for listening closely to one sentence,
// "read-along" at a natural pace for listening to the whole story.
export type SentenceNarration = "slow" | "read-along";

// fetchSentenceAudioUrl fetches the TTS audio of one sentence and returns an
// object URL for playback. A plain <audio src> can't be used because
// generated stories require the Authorization header, which media elements
// cannot send - so we fetch the bytes ourselves. The caller owns the returned
// URL and must revoke it with URL.revokeObjectURL when done.
export async function fetchSentenceAudioUrl(
  storyId: string,
  locale: string,
  sentenceIdx: number,
  narration: SentenceNarration = "slow",
  signal?: AbortSignal,
): Promise<string> {
  const url = apiUrl("/audio");
  url.searchParams.append("story_id", storyId);
  url.searchParams.append("locale", locale);
  url.searchParams.append("sentence_idx", sentenceIdx.toString());
  url.searchParams.append("narration", narration);

  const params = await fetchParamsWithOptionalAuth("GET");
  const res = await fetch(url, { ...params, signal });
  // Read-along of the user's own stories counts against a daily limit.
  await throwIfRefused(res);
  if (!res.ok) {
    console.error("Unexpected result for", url);
    console.error(res);
    throw new Error("Unexpected result for audio fetch");
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// useProgressLinesQuery fetches the playful status lines rotated in the
// story-generation progress overlay. Unauthenticated; `enabled` is meant to
// be wired to the generate mutation's isPending so the request only fires
// when the overlay is actually shown. The lines are static server content,
// so a fetched set is kept for the whole session (staleTime: Infinity).
export function useProgressLinesQuery(
  l: string,
  moods: string[],
  enabled: boolean,
) {
  const url = apiUrl("/progress-lines");
  url.searchParams.append("l", l);
  url.searchParams.append("moods", moods.join(","));

  return useQuery<string[]>({
    queryKey: ["progress-lines", l, moods],
    queryFn: async () =>
      fetch(url).then((res) => {
        if (res.ok) {
          return res.json().then((obj) => obj.lines ?? []);
        } else {
          console.error("Unexpected result for", url);
          console.error(res);
          throw new Error("Unexpected result for progress-lines query");
        }
      }),
    enabled,
    staleTime: Infinity,
    retry: 1,
  });
}

export interface WordExplanation {
  // The explanation in explanation markup (see explanationMarkup.tsx).
  markup: string;
  // The global dictionary sense this word was resolved to, or null while it is
  // being prepared or if the word couldn't be added to the dictionary.
  dictionaryEntryId: number | null;
  // The API is still preparing the dictionary entry, or recording the lookup
  // against it: the explanation is re-fetched until it's done (see
  // preparingRefetchInterval).
  dictionaryEntryPreparing: boolean;
  // Whether the logged-in user already has this sense in their saved-word list.
  // Always false for anonymous users; used to render the Save button as
  // already-saved.
  alreadySaved: boolean;
  // The clearly fixed formula the word is part of ("Guten Morgen" for
  // "Morgen"), which gets its own Save button; null for anything else.
  phrase: {
    dictionaryEntryId: number;
    displayForm: string;
    alreadySaved: boolean;
  } | null;
}

// Past the API's own bound on preparing an entry (ingestTimeout in
// api/src/explanation/ingest.go), re-fetching can't help; reopening the popup
// starts over.
const PREPARING_REFETCH_LIMIT_MS = 130_000;

// preparingRefetchInterval is the explain query's refetchInterval: while the
// dictionary entry is being prepared, the explanation is re-fetched, less often
// the longer it takes (most entries are ready within seconds), until
// PREPARING_REFETCH_LIMIT_MS after preparingSince, when the popup first got
// the explanation with its entry still being prepared.
export function preparingRefetchInterval(
  explanation: WordExplanation | undefined,
  preparingSince: number,
  now: number,
): number | false {
  if (!explanation?.dictionaryEntryPreparing) return false;
  const preparingFor = now - preparingSince;
  if (preparingFor >= PREPARING_REFETCH_LIMIT_MS) return false;
  if (preparingFor < 6_000) return 1500;
  if (preparingFor < 20_000) return 3000;
  return 10_000;
}

// isPreparingRefetch tells whether a fetch of the explanation only re-fetches
// it for its dictionary entry, which the API doesn't take for another lookup:
// the popup, opened at openedAt, already got the cached explanation (at
// cachedAt) and it said the entry is being prepared. Opening the popup again
// is another lookup.
export function isPreparingRefetch(
  cached: WordExplanation | undefined,
  cachedAt: number,
  openedAt: number,
): boolean {
  return cached?.dictionaryEntryPreparing === true && cachedAt >= openedAt;
}

export function useWordExplainQuery(
  storyId: string,
  l: string,
  r: string,
  lSentenceIdx: number,
  rSentenceIdx: number,
  wordIdx: number,
) {
  const queryClient = useQueryClient();
  const [openedAt] = useState(() => Date.now());
  const preparingSinceRef = useRef<number | null>(null);
  let url = new URL(`${API_URL}/explain`);
  url.searchParams.append("story_id", storyId);
  url.searchParams.append("l", l);
  url.searchParams.append("r", r);
  url.searchParams.append("l_sentence_idx", lSentenceIdx.toString());
  url.searchParams.append("r_sentence_idx", rSentenceIdx.toString());
  url.searchParams.append("word_idx", wordIdx.toString());

  return useQuery<WordExplanation>({
    queryKey: [
      "explain-word",
      storyId,
      l,
      r,
      lSentenceIdx,
      rSentenceIdx,
      wordIdx,
    ],
    queryFn: async ({ queryKey }) => {
      const requestUrl = new URL(url);
      const cached = queryClient.getQueryState<WordExplanation>(queryKey);
      if (
        isPreparingRefetch(cached?.data, cached?.dataUpdatedAt ?? 0, openedAt)
      ) {
        requestUrl.searchParams.append("refetch", "1");
      }
      return fetchParamsWithOptionalAuth("GET").then((params) =>
        fetch(requestUrl, params).then(async (res) => {
          if (res.ok) {
            return res.json().then((obj) => {
              return {
                // An API from before the markup sends only the plain text.
                markup:
                  typeof obj.markup === "string"
                    ? obj.markup
                    : escapeExplanationText(obj.content ?? ""),
                dictionaryEntryId: obj.dictionary_entry_id ?? null,
                dictionaryEntryPreparing:
                  obj.dictionary_entry_preparing ?? false,
                alreadySaved: obj.already_saved ?? false,
                phrase: obj.phrase
                  ? {
                      dictionaryEntryId: obj.phrase.dictionary_entry_id,
                      displayForm: obj.phrase.display_form,
                      alreadySaved: obj.phrase.already_saved ?? false,
                    }
                  : null,
              };
            });
          }
          throw await httpErrorFromResponse(requestUrl, res);
        }),
      );
    },
    refetchInterval: (query) => {
      const now = Date.now();
      if (
        query.state.data?.dictionaryEntryPreparing &&
        preparingSinceRef.current === null
      ) {
        preparingSinceRef.current = now;
      }
      return preparingRefetchInterval(
        query.state.data,
        preparingSinceRef.current ?? now,
        now,
      );
    },
    // The API already retries its LLM calls, so only a request that never
    // got an answer (fetch rejects with a TypeError) is worth one more try.
    retry: (failureCount, error) =>
      failureCount < 1 && error instanceof TypeError,
  });
}

// DictionaryWord is one row of the dictionary page: a word the user saved,
// looked up in a story or practiced. briefMeaning is an empty string until the
// description in the user's spoken language exists. The strengths (0..1) say
// how well practice says the user knows each direction; 0 until practiced.
export interface DictionaryWord {
  dictionaryEntryId: number;
  displayForm: string;
  partOfSpeech: string;
  gender: string;
  examples: string[];
  briefMeaning: string;
  saved: boolean;
  practiced: boolean;
  leech: boolean;
  recognizeStrength: number;
  produceStrength: number;
}

export interface MyDictionaryPage {
  words: DictionaryWord[];
  page: number;
  hasMore: boolean;
}

// "saved" is My Dictionary; "all" adds every word the user looked up or
// practiced.
export type DictionaryFilter = "saved" | "all";
export type DictionarySort = "recent" | "weakest" | "strongest";

export const MY_DICTIONARY_PAGE_SIZE = 100;

// useMyDictionaryQuery fetches one page (MY_DICTIONARY_PAGE_SIZE words) of the
// user's words in learned language r, localized to spoken language l.
// page is 0-based. Previous page data is kept while the next page loads to
// avoid a flash of empty content during pagination.
export function useMyDictionaryQuery(
  l: string,
  r: string,
  page: number,
  filter: DictionaryFilter,
  sort: DictionarySort,
) {
  const url = apiUrl("/my-dictionary");
  url.searchParams.append("l", l);
  url.searchParams.append("r", r);
  url.searchParams.append("page", page.toString());
  url.searchParams.append("filter", filter);
  url.searchParams.append("sort", sort);

  return useQuery<MyDictionaryPage>({
    queryKey: ["my-dictionary", l, r, page, filter, sort],
    queryFn: async () =>
      fetchParamsWithAuth("GET").then((params) =>
        fetch(url, params).then(async (res) => {
          if (res.ok) {
            return res.json().then((obj) => ({
              words: (obj.words ?? []).map((w: Record<string, unknown>) => ({
                dictionaryEntryId: w.dictionary_entry_id as number,
                displayForm: w.display_form as string,
                partOfSpeech: w.part_of_speech as string,
                gender: w.gender as string,
                examples: (w.examples as string[]) ?? [],
                briefMeaning: (w.brief_meaning as string) ?? "",
                saved: (w.saved as boolean) ?? false,
                practiced: (w.practiced as boolean) ?? false,
                leech: (w.leech as boolean) ?? false,
                recognizeStrength: (w.recognize_strength as number) ?? 0,
                produceStrength: (w.produce_strength as number) ?? 0,
              })),
              page: obj.page ?? page,
              hasMore: obj.has_more ?? false,
            }));
          }
          throw await httpErrorFromResponse(url, res);
        }),
      ),
    placeholderData: keepPreviousData,
  });
}
